import type { Prescription } from '@/services/prescriptionApi';

/**
 * Statuses where the patient is on, or about to receive, the drug.
 *
 * This mirrors `ACTIVE_RX_STATUSES` in the backend
 * (`medication-reminders.service.ts` and `portal-assistant-context.service.ts`
 * share the same list, and the patient portal's prescription list filters on
 * it), which makes it the closest thing to a source of truth.
 *
 * It exists because the chart currently disagrees with itself three ways:
 *
 *   - `PatientInsightsRail`  allow-lists signed / transmitted / dispensed —
 *     a correct but narrow subset, so a script that is `ready-for-pickup`
 *     silently drops out of "Active Medications".
 *   - `PatientTimelineSpine` allow-lists active / transmitted /
 *     pharmacy_received — and two of those three are not values the
 *     backend's `PrescriptionStatus` union can produce, so that list is
 *     mostly matching nothing.
 *   - `PatientDetailsPage`   deny-lists cancelled / expired / discontinued /
 *     draft / legacy_imported — the broadest of the three, so the header's
 *     med count can exceed the rail's list for the same patient.
 *
 * The three are deliberately left in place: changing them would change the
 * existing chart, which has to stay byte-identical while SIMPLE_CHART_ENABLED
 * is off. Reconciling them is a follow-up.
 */
const ACTIVE_RX_STATUSES: ReadonlySet<string> = new Set([
  'signed',
  'transmitted',
  'received-by-pharmacy',
  'in-progress',
  'ready-for-pickup',
  'dispensed',
]);

/**
 * Does the *status* say this script is live?
 *
 * Allow-list, not deny-list: an unrecognised status reads as NOT active. On
 * a medication list that is the safe direction — showing a drug the patient
 * has stopped is worse than omitting one, because the visible list is what a
 * clinician prescribes against.
 *
 * This is only half the question. A status never changes on its own, so a
 * seven-day course signed six months ago is still `signed` today and this
 * predicate still says yes. Use `prescriptionActivity` for "is the patient
 * taking this *now*", and keep this one for "was this script ever live".
 */
export function isActivePrescription(rx: Prescription): boolean {
  return ACTIVE_RX_STATUSES.has((rx.status || '').toLowerCase());
}

// ---------- is the course still running? ----------

export type RxActivity =
  /** Status says it never went live, or it was cancelled/expired/stopped. */
  | { state: 'inactive' }
  /**
   * The patient should be taking this today. `endsOn` is null when no
   * duration was ever recorded — see `courseEnd` for why that still counts
   * as taking it.
   */
  | { state: 'taking'; endsOn: Date | null; daysLeft: number | null }
  /** The course ran out. Still real history, just not current. */
  | { state: 'finished'; endedOn: Date; reason: 'course' | 'expired' };

const DAYS_PER_UNIT: Record<string, number> = {
  day: 1,
  days: 1,
  d: 1,
  week: 7,
  weeks: 7,
  wk: 7,
  wks: 7,
  w: 7,
  month: 30,
  months: 30,
  mo: 30,
  mos: 30,
  m: 30,
  year: 365,
  years: 365,
  y: 365,
};

const MS_PER_DAY = 86_400_000;

const asDate = (v: unknown): Date | null => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(String(v));
  return Number.isNaN(d.getTime()) ? null : d;
};

/** When the script was written — `createdAt` is the fallback for legacy rows. */
function startOf(rx: Prescription): Date | null {
  return asDate(rx.writtenDate) ?? asDate(rx.createdAt);
}

/**
 * How many days of drug the script covers, or null when nothing recorded it.
 *
 * `daysSupply` is deliberately checked for `> 0`, not just presence: the
 * AI suggested-actions commit path writes `daysSupply: 0` as its "unknown"
 * value, and reading that as a zero-day course would retire every
 * AI-written script the moment it was created.
 *
 * Authorised refills extend the supply. Using *authorised* rather than
 * *remaining* keeps a drug on the list longer, which is the safe direction
 * — a refill the patient hasn't collected yet shouldn't end the course.
 */
function courseDays(rx: Prescription): number | null {
  let base: number | null = null;

  if (typeof rx.duration === 'number' && rx.duration > 0) {
    const unit = (rx.durationUnit || 'days').trim().toLowerCase();
    const perUnit = DAYS_PER_UNIT[unit];
    // An unrecognised unit is missing data, not one day.
    if (perUnit) base = rx.duration * perUnit;
  }

  if (base === null && typeof rx.daysSupply === 'number' && rx.daysSupply > 0) {
    base = rx.daysSupply;
  }

  if (base === null) return null;

  const refills =
    typeof rx.refillsAuthorized === 'number' && rx.refillsAuthorized > 0
      ? rx.refillsAuthorized
      : 0;
  return base * (1 + refills);
}

/**
 * The day the course runs out, or null when the script records no duration.
 *
 * A null here does NOT mean "stopped". An undated script stays on the
 * medication list until something says otherwise, because dropping a drug
 * the patient is still taking is the failure that harms someone — the
 * doctor prescribes into an interaction they were never shown. A stale
 * entry is visible and dated; a missing one is invisible.
 *
 * `expirationDate` (written date + one year, everywhere that creates a
 * script) is the backstop that stops "undated" meaning "forever".
 */
export function courseEnd(rx: Prescription): Date | null {
  const start = startOf(rx);
  const days = courseDays(rx);
  if (!start || days === null) return null;
  return new Date(start.getTime() + days * MS_PER_DAY);
}

/**
 * Is the patient on this drug today?
 *
 * Status first — a cancelled script is never current whatever its dates
 * say. Then the course window, then the script's own expiry as a backstop
 * for scripts that recorded no duration.
 */
export function prescriptionActivity(
  rx: Prescription,
  now: Date = new Date(),
): RxActivity {
  if (!isActivePrescription(rx)) return { state: 'inactive' };

  const end = courseEnd(rx);
  if (end) {
    if (end.getTime() < now.getTime()) {
      return { state: 'finished', endedOn: end, reason: 'course' };
    }
    return {
      state: 'taking',
      endsOn: end,
      daysLeft: Math.max(
        0,
        Math.ceil((end.getTime() - now.getTime()) / MS_PER_DAY),
      ),
    };
  }

  // No duration recorded. Fall back to the script's validity window.
  const expires = asDate(rx.expirationDate);
  if (expires && expires.getTime() < now.getTime()) {
    return { state: 'finished', endedOn: expires, reason: 'expired' };
  }

  return { state: 'taking', endsOn: null, daysLeft: null };
}

/** Shorthand for the medication list: on it today? */
export function isCurrentlyTaking(rx: Prescription, now?: Date): boolean {
  return prescriptionActivity(rx, now).state === 'taking';
}
